import React, { useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import ClearIcon from '@material-ui/icons/Clear';
import IconButton from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/IconButton';
import Button from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Button';
import ConfirmationDialog from 'Components/UI/ConfirmationDialog';
import { shortDateAtTime } from 'Consts/DateFormats';
import { displayErrorMessage, displaySuccess } from 'Helpers/FacetNotificationManager';
import useMobileLayout from 'Hooks/useMobileLayout';
import ScheduleSendDatePicker from '../ScheduleSendDatePicker/ScheduleSendDatePicker';
import ScheduleSendReview from '../ScheduleSendReview';

const styles = (theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
    borderRadius: theme.spacing(1),
    flexBasis: '48%',
    maxWidth: '48%',
    cursor: 'pointer',
  },
  rootMobile: {
    flexBasis: '100%',
    maxWidth: '100%',
  },
  messageDetail: {
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  messageActions: {
    flexShrink: 0,
    marginLeft: theme.spacing(2),
  },
  removeButton: {
    marginLeft: theme.spacing(1),
  },
  detailText: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

const ConflictingMessageListItem = ({
  className,
  classes,
  meetingAgendaResource,
  refreshMeetingAgendas,
  rootNodeProps,
  logEventSuccess,
  logEventFailure,
}) => {
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [reviewScheduleSendModalOpen, setReviewScheduleSendModalOpen] = useState(false);
  const [scheduledSendDatePickerIsOpen, setScheduledSendDatePickerIsOpen] = useState(false);
  const isMobile = useMobileLayout();

  const deleteMeetingAgenda = async () => {
    try {
      await meetingAgendaResource.delete();
      logEventSuccess('Deleted a Scheduled Meeting Agenda', { meetingAgendaId: meetingAgendaResource.id() });
      displaySuccess('Deleted the Scheduled Email');
    } catch (err) {
      logEventFailure('Failed to deleted a Scheduled Meeting Agenda', { meetingAgendaId: meetingAgendaResource.id() });
      displayErrorMessage(err, err.errors);
    }
    refreshMeetingAgendas();
  };

  const rescheduleMeetingAgenda = async meetingAgendaRes => {
    try {
      await meetingAgendaRes.submit();
      logEventSuccess('Rescheduled a scheduled email', { meetingAgendaId: meetingAgendaRes.id() });
      displaySuccess('Email successfully rescheduled');
      refreshMeetingAgendas();
    } catch (e) {
      logEventFailure('Failed to rescheduled Meeting Agenda', { meetingAgendaId: meetingAgendaRes.id() });
      displayErrorMessage(e);
    }
  };

  return (
    <div
      className={classNames(classes.root, className, {
        [classes.rootMobile]: isMobile,
      })}
      {...rootNodeProps}
      test={'out-of-office-conflicting-message-item'}
    >
      <ScheduleSendDatePicker
        isOpen={scheduledSendDatePickerIsOpen}
        closeModal={() => {
          setScheduledSendDatePickerIsOpen(false);
        }}
        meetingAgendaResource={meetingAgendaResource}
        onSubmit={rescheduleMeetingAgenda}
      />
      <ScheduleSendReview
        meetingAgendaResource={meetingAgendaResource}
        isOpen={reviewScheduleSendModalOpen}
        closeModal={() => setReviewScheduleSendModalOpen(false)}
        refreshResources={refreshMeetingAgendas}
      />
      {showConfirmationDialog && (
        <ConfirmationDialog
          onConfirm={async () => {
            await deleteMeetingAgenda();
            setShowConfirmationDialog(false);
          }}
          onCancel={() => {
            setShowConfirmationDialog(false);
          }}
          confirmLabel="Confirm"
          message={`You are about to delete the Scheduled Email to ${meetingAgendaResource.attr('householdName')} with subject: ${meetingAgendaResource.attr('subject')}`}
        />
      )}
      <div
        className={classes.messageDetail}
        tabIndex={0}
        role="button"
        onKeyDown={() => { }}
        onClick={() => setReviewScheduleSendModalOpen(true)}
      >
        <Typography
          weight={'bold'}
          color="primary"
          align="left"
          variant="h5"
          noWrap
        >
          {meetingAgendaResource.attr('householdName')}
        </Typography>
        <Typography
          className={classNames(classes.title, classes.messageDetail)}
          color={'primaryGray'}
          align="left"
          variant="h5"
          noWrap
        >
          {meetingAgendaResource.attr('subject')}
        </Typography>
        <Typography
          className={classNames(classes.title, classes.messageDetail)}
          color={'primaryGray'}
          align="left"
          variant="h6"
          noWrap
        >
          {moment(meetingAgendaResource.attr('scheduledSendCustomDate')).format(shortDateAtTime)}
        </Typography>
      </div>
      <div className={classes.messageActions}>
        <Button
          variant={'contained'}
          color={'orange'}
          onClick={(event) => {
            event.stopPropagation();
            setScheduledSendDatePickerIsOpen(true);
          }}
          test={'out-of-office-reschedule-conflicting-message'}
        >
          {'Reschedule'}
        </Button>
        <IconButton
          className={classes.removeButton}
          size="small"
          title="Remove scheduled message"
          icon={ClearIcon}
          onClick={(event) => {
            event.stopPropagation();
            setShowConfirmationDialog(true);
          }}
          test={'out-of-office-remove-scheduled-message-button'}
        />
      </div>
    </div>
  );
};

ConflictingMessageListItem.defaultProps = {
  className: '',
};

ConflictingMessageListItem.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  meetingAgendaResource: PropTypes.object.isRequired,
  rootNodeProps: PropTypes.object.isRequired,
  logEventSuccess: PropTypes.func.isRequired,
  logEventFailure: PropTypes.func.isRequired,
};

export default withStyles(styles)(ConflictingMessageListItem);
